import { ArdoqId } from '@ardoq/api-types';
import {
  FieldType,
  getPossibleComponentTypes,
  hasChildren,
} from '@ardoq/renderers';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { getFieldOptions } from 'appModelStateEdit/propertiesEditor/fieldUtils';
import { RIGID_TYPE_WARNING_MSG } from 'models/consts';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { componentAccessControlOperation } from 'resourcePermissions/accessControlHelpers/component';
import { PermissionContext } from '@ardoq/access-control';
import { getActiveScenarioId } from 'streams/activeScenario/activeScenario$';
import { getFeatureContext } from 'streams/featureContext$';

const getParentOptions = (
  componentId: ArdoqId,
  enhancedScopeData: EnhancedScopeData,
  permissionContext: PermissionContext
) => {
  const scenarioId = getActiveScenarioId();
  const possibleParents = enhancedScopeDataOperations.getPossibleParents(
    enhancedScopeData,
    componentId
  );
  const validParentComponents = scenarioId
    ? possibleParents
    : possibleParents.filter(component =>
        componentAccessControlOperation.canEditComponent({
          component,
          permissionContext,
          featureContext: getFeatureContext(),
        })
      );
  return [
    {
      value: null,
      label: 'None',
    },
    ...validParentComponents.map(({ _id: value, name: label }) => ({
      value,
      label,
    })),
  ];
};

const getTypeOptions = (
  componentId: ArdoqId,
  enhancedScopeData: EnhancedScopeData
) => {
  const component = enhancedScopeData.componentsById[componentId];
  const isFlexible =
    enhancedScopeData.modelsById[component.model].flexible ||
    !hasChildren(component._id, enhancedScopeData);
  if (!isFlexible) {
    return [
      {
        value: component.typeId,
        label: RIGID_TYPE_WARNING_MSG,
      },
    ];
  }
  return getPossibleComponentTypes(componentId, enhancedScopeData).map(
    type => ({
      value: type.id,
      label: type.name,
    })
  );
};

export const defaultPropertyOptionGetterMap = new Map([
  [FieldType.PARENT, getParentOptions],
  [FieldType.TYPE_ID_COMPONENT, getTypeOptions],
]);

export const customPropertyOptionGetterMap = new Map([
  [FieldType.LIST, getFieldOptions],
  [FieldType.SELECT_MULTIPLE_LIST, getFieldOptions],
]);
